import React, { useState, useEffect } from 'react'
import { 
  FolderOpen, 
  Plus, 
  Upload, 
  CheckSquare, 
  PenTool, 
  Trash2, 
  Settings,
  AlertCircle,
  CheckCircle,
  Target,
  Sliders,
  BookOpen,
  ArrowRight,
  Info,
  Menu,
  X,
  ChevronRight,
  BarChart3,
  Download,
  FileText,
  Edit3,
  Database,
  ImageIcon,
  FolderPlus,
  Shield,
  Package,
  Brain,
  Clock,
  RotateCcw
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/utils/cn'
import Tooltip from '@/components/Tooltip'
import { useTranslation } from 'react-i18next'

const ManualPage: React.FC = () => {
  const { t } = useTranslation()
  const { isAdmin } = useAuth()
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024)
  const [activeSection, setActiveSection] = useState('')

  // Table of contents structure
  const tableOfContents = [
    {
      id: 'rooms-sessions',
      title: t('manual.sections.rooms_sessions'),
      icon: <FolderOpen className="h-4 w-4" />,
      subsections: [
        { id: 'what-is-room', title: t('manual.sections.what_is_room') },
        { id: 'what-is-session', title: t('manual.sections.what_is_session') }
      ]
    },
    {
      id: 'creating-session',
      title: t('manual.sections.creating_session'),
      icon: <Plus className="h-4 w-4" />,
      subsections: []
    },
    {
      id: 'session-workflow',
      title: t('manual.sections.session_workflow'),
      icon: <Target className="h-4 w-4" />,
      subsections: [
        { id: 'workflow-process', title: t('manual.sections.workflow_process') }
      ]
    },
    {
      id: 'uploading-images',
      title: t('manual.sections.uploading_images'),
      icon: <Upload className="h-4 w-4" />,
      subsections: [
        { id: 'upload-process', title: t('manual.sections.upload_process') }
      ]
    },
    {
      id: 'working-detections',
      title: t('manual.sections.working_detections'),
      icon: <Target className="h-4 w-4" />,
      subsections: [
        { id: 'confidence-levels', title: t('manual.sections.confidence_levels') }
      ]
    },
    {
      id: 'correcting-classifications',
      title: t('manual.sections.correcting_classifications'),
      icon: <CheckCircle className="h-4 w-4" />,
      subsections: []
    },
    {
      id: 'bulk-operations',
      title: 'Bulk Operations',
      icon: <Trash2 className="h-4 w-4" />,
      subsections: []
    },
    {
      id: 'drawing-detections',
      title: t('manual.sections.drawing_detections'),
      icon: <PenTool className="h-4 w-4" />,
      subsections: [
        { id: 'manual-detection', title: t('manual.sections.manual_detection') },
        { id: 'drawing-tips', title: t('manual.sections.drawing_tips') }
      ]
    },
    {
      id: 'deleting-detections',
      title: t('manual.sections.deleting_detections'),
      icon: <Trash2 className="h-4 w-4" />,
      subsections: []
    },
    ...(isAdmin ? [{
      id: 'algorithm-settings',
      title: t('manual.sections.algorithm_settings') + ' (Admin)',
      icon: <Settings className="h-4 w-4" />,
      subsections: [
        { id: 'detection-parameters', title: t('manual.sections.detection_parameters') },
        { id: 'classification-parameters', title: t('manual.sections.classification_parameters') }
      ]
    }] : []),
    {
      id: 'staging-changes',
      title: t('manual.sections.staging_changes'),
      icon: <CheckSquare className="h-4 w-4" />,
      subsections: [
        { id: 'when-to-stage', title: t('manual.sections.when_to_stage') },
        { id: 'staging-process', title: t('manual.sections.staging_process') }
      ]
    },
    {
      id: 'reports-management',
      title: t('manual.sections.reports_management'),
      icon: <BarChart3 className="h-4 w-4" />,
      subsections: [
        { id: 'creating-reports', title: t('manual.sections.creating_reports') },
        { id: 'editing-reports', title: t('manual.sections.editing_reports') },
        { id: 'exporting-reports', title: t('manual.sections.exporting_reports') }
      ]
    },
    ...(isAdmin ? [{
      id: 'catalog-management',
      title: t('manual.sections.catalog_management'),
      icon: <Database className="h-4 w-4" />,
      subsections: [
        { id: 'inventory-catalog', title: t('manual.sections.inventory_catalog') },
        { id: 'adding-items', title: t('manual.sections.adding_items') },
        { id: 'editing-items', title: t('manual.sections.editing_items') },
        { id: 'adding-examples', title: t('manual.sections.adding_examples') },
        { id: 'rebuilding-index', title: t('manual.sections.rebuilding_index') }
      ]
    }] : []),
    {
      id: 'quick-reference',
      title: t('manual.sections.quick_reference'),
      icon: <Sliders className="h-4 w-4" />,
      subsections: [
        { id: 'keyboard-shortcuts', title: t('manual.sections.keyboard_shortcuts') },
        { id: 'color-guide', title: t('manual.sections.color_guide') },
        { id: 'need-help', title: t('manual.sections.need_help') }
      ]
    }
  ]

  // Handle section navigation
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - 80 // Account for fixed header
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
      setActiveSection(sectionId)
    }
  }

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.flatMap(section => [
        section.id,
        ...section.subsections.map(sub => sub.id)
      ])
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId)
        if (element) {
          const rect = element.getBoundingClientRect()
          if (rect.top >= 80 && rect.top <= 280) { // Adjust for fixed header
            setActiveSection(sectionId)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Sidebar component
  const Sidebar = () => (
    <div className={cn(
      'fixed top-16 bottom-0 left-0 z-40 w-80 bg-white shadow-lg transform transition-transform duration-300 lg:sticky lg:top-20 lg:h-screen lg:translate-x-0 flex flex-col',
      sidebarOpen ? 'translate-x-0' : '-translate-x-full'
    )}>
      <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
        <div className="flex items-center">
          <BookOpen className="h-6 w-6 text-primary-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">{t('manual.table_of_contents')}</h2>
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="p-6 overflow-y-auto flex-1">
        <div className="space-y-2">
          {tableOfContents.map((section) => (
            <div key={section.id}>
              <button
                onClick={() => scrollToSection(section.id)}
                className={cn(
                  'flex items-center w-full p-3 text-left rounded-lg transition-colors',
                  activeSection === section.id
                    ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <div className="flex items-center flex-1">
                  {section.icon}
                  <span className="ml-3 text-sm font-medium">{section.title}</span>
                </div>
                {section.subsections.length > 0 && (
                  <ChevronRight className="h-4 w-4 opacity-50" />
                )}
              </button>
              
              {section.subsections.length > 0 && (
                <div className="ml-4 space-y-2 mt-1">
                  {section.subsections.map((subsection) => (
                    <button
                      key={subsection.id}
                      onClick={() => scrollToSection(subsection.id)}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                        activeSection === subsection.id
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      )}
                    >
                      {subsection.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>
    </div>
  )


  // Sample status badges matching actual components
  const StatusBadge: React.FC<{ status: 'in-progress' | 'completed' }> = ({ status }) => (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
      status === 'completed'
        ? 'bg-green-100 text-green-800'
        : 'bg-blue-100 text-blue-800'
    )}>
      {status === 'completed' ? t('manual.status_badges.completed') : t('manual.status_badges.in_progress')}
    </span>
  )

  // Sample detection confidence badges
  const ConfidenceBadge: React.FC<{ type: 'confident' | 'uncertain' | 'corrected' }> = ({ type }) => (
    <div className={cn(
      "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
      type === 'confident' ? "bg-green-600 text-white" :
      type === 'corrected' ? "bg-purple-600 text-white" :
      "bg-orange-600 text-white"
    )}>
      {type === 'corrected' && <span className="mr-1">✓</span>}
      {type === 'confident' ? '85.3%' : type === 'corrected' ? '19.7%' : '45.2%'}
    </div>
  )

  // Sample action buttons matching actual UI
  const ActionButton: React.FC<{ 
    icon: React.ReactNode
    label: string
    variant: 'stage' | 'draw' | 'settings'
    disabled?: boolean
  }> = ({ icon, label, variant, disabled = false }) => (
    <Tooltip content={label} position="bottom">
      <button
        disabled={disabled}
        className={cn(
          "flex items-center justify-center w-10 h-10 rounded-lg border transition-all",
          variant === 'stage' && !disabled
            ? "bg-green-100 border-green-300 text-green-700 hover:bg-green-200"
            : variant === 'draw' && !disabled
            ? "bg-blue-100 border-blue-300 text-blue-700"
            : variant === 'settings'
            ? "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
            : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
        )}
      >
        {icon}
      </button>
    </Tooltip>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar />
      {/* Main Content */}
      <div className="flex-1 lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white shadow-sm border-b px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Manual</h1>
          <div className="w-9" /> {/* Spacer */}
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-primary-600" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">{t('manual.title')}</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('manual.subtitle')}
            </p>
          </div>

          {/* Overview */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Info className="h-5 w-5 mr-2 text-primary-600" />
              {t('manual.overview_title')}
            </h2>
            <p className="text-gray-600">
              Welcome to LUMIS - your AI-powered precision solution for boutique inventory management. This comprehensive guide covers 
              everything you need to know about using the luxury item recognition and cataloging system, from creating your first analysis 
              session to managing advanced detection workflows. 
            </p>
            <p className="text-gray-600 mt-4">
              Use the sidebar navigation to explore each section, or scroll through this complete guide to master your boutique inventory system 
              with confidence scoring, secure processing, and scalable collection management.
            </p>
          </div>

          {/* Section 1: Understanding Rooms and Sessions */}
          <div id="rooms-sessions" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <FolderOpen className="h-6 w-6 mr-3 text-primary-600" />
              {t('manual.content.rooms_sessions_title')}
            </h2>
            
            <div className="space-y-6">
              <div id="what-is-room">
                <h3 className="text-lg font-medium text-gray-800 mb-3">{t('manual.sections.what_is_room')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('manual.content.room_description')}
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>{t('manual.content.room_example').split(':')[0]}:</strong> {t('manual.content.room_example').split(':')[1]}
                  </p>
                </div>
              </div>

              <div id="what-is-session">
                <h3 className="text-lg font-medium text-gray-800 mb-3">{t('manual.sections.what_is_session')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('manual.content.session_description')}
                </p>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <StatusBadge status="in-progress" />
                    <span className="text-sm text-gray-600">{t('manual.content.session_status_active')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <StatusBadge status="completed" />
                    <span className="text-sm text-gray-600">{t('manual.content.session_status_completed')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Creating a Session */}
          <div id="creating-session" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Plus className="h-6 w-6 mr-3 text-primary-600" />
              {t('manual.content.creating_session_title')}
            </h2>
            
            <div className="space-y-4">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-800">{t('manual.content.step_navigate_sessions')}</h4>
                  <p className="text-gray-600 text-sm">{t('manual.content.step_navigate_sessions_desc')}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800">{t('manual.content.step_create_new_session')}</h4>
                  <p className="text-gray-600 text-sm">{t('manual.content.step_create_new_session_desc')}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800">{t('manual.content.step_select_room')}</h4>
                  <p className="text-gray-600 text-sm">{t('manual.content.step_select_room_desc')}</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                  <div>
                    <p className="text-blue-800 text-sm">
                      <strong>{t('manual.content.note_room_required').split(':')[0]}:</strong> {t('manual.content.note_room_required').split(':')[1]}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Session Workflow */}
          <div id="session-workflow" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Target className="h-6 w-6 mr-3 text-primary-600" />
              {t('manual.content.session_workflow_title')}
            </h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <StatusBadge status="in-progress" />
                    <span className="font-medium text-gray-800">{t('manual.status_badges.in_progress')}</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• {t('manual.content.in_progress_features.can_upload')}</li>
                    <li>• {t('manual.content.in_progress_features.can_modify')}</li>
                    <li>• {t('manual.content.in_progress_features.can_draw')}</li>
                    <li>• {t('manual.content.in_progress_features.can_delete')}</li>
                    <li>• {t('manual.content.in_progress_features.can_adjust')}</li>
                  </ul>
                </div>
                
                <div className="border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <StatusBadge status="completed" />
                    <span className="font-medium text-gray-800">{t('manual.status_badges.completed')}</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• {t('manual.content.completed_features.read_only')}</li>
                    <li>• {t('manual.content.completed_features.no_uploads')}</li>
                    <li>• {t('manual.content.completed_features.no_modify')}</li>
                    <li>• {t('manual.content.completed_features.frozen_results')}</li>
                    <li>• {t('manual.content.completed_features.can_navigate')}</li>
                  </ul>
                </div>
              </div>

              <div id="workflow-process" className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6">
                <h4 className="font-medium text-gray-800 mb-4 flex items-center">
                  <ArrowRight className="h-5 w-5 mr-2 text-primary-600" />
                  {t('manual.sections.workflow_process')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">{t('manual.content.workflow_steps.create_session')}</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">{t('manual.content.workflow_steps.upload_analyze')}</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">{t('manual.content.workflow_steps.review_correct')}</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700">{t('manual.content.workflow_steps.stage_changes')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Uploading Images */}
          <div id="uploading-images" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Upload className="h-6 w-6 mr-3 text-primary-600" />
              {t('manual.content.uploading_images_title')}
            </h2>
            
            <div className="space-y-6">
              <div id="upload-process">
                <h3 className="text-lg font-medium text-gray-800 mb-3">{t('manual.sections.upload_process')}</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-800">{t('manual.content.upload_step_1')}</h4>
                    <p className="text-gray-600 text-sm">{t('manual.content.upload_step_1_desc')}</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-800">{t('manual.content.upload_step_2')}</h4>
                    <p className="text-gray-600 text-sm">{t('manual.content.upload_step_2_desc')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                  <div>
                    <p className="text-yellow-800 text-sm">
                      <strong>{t('manual.content.upload_important_note').split(':')[0]}:</strong> {t('manual.content.upload_important_note').split(':')[1]}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Working with Detections */}
          <div id="working-detections" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Target className="h-6 w-6 mr-3 text-primary-600" />
              {t('manual.content.working_detections_title')}
            </h2>
            
            <div className="space-y-6">
              <div id="confidence-levels">
                <h3 className="text-lg font-medium text-gray-800 mb-4">{t('manual.sections.confidence_levels')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border border-green-200 rounded-lg p-4 text-center">
                    <ConfidenceBadge type="confident" />
                    <h4 className="font-medium text-gray-800 mt-2 mb-1">{t('manual.status_badges.confident')}</h4>
                    <p className="text-sm text-gray-600">{t('manual.content.confident_desc')}</p>
                  </div>
                  
                  <div className="border border-orange-200 rounded-lg p-4 text-center">
                    <ConfidenceBadge type="uncertain" />
                    <h4 className="font-medium text-gray-800 mt-2 mb-1">{t('manual.status_badges.uncertain')}</h4>
                    <p className="text-sm text-gray-600">{t('manual.content.uncertain_desc')}</p>
                  </div>
                  
                  <div className="border border-purple-200 rounded-lg p-4 text-center">
                    <ConfidenceBadge type="corrected" />
                    <h4 className="font-medium text-gray-800 mt-2 mb-1">{t('manual.status_badges.user_corrected')}</h4>
                    <p className="text-sm text-gray-600">{t('manual.content.user_corrected_desc')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">💡 Pro Tip</h4>
                <p className="text-blue-700 text-sm">
                  Click on any detection box to see detailed information including confidence scores, classification options, and correction history.
                </p>
              </div>
            </div>
          </div>

          {/* Section 6: Correcting Classifications */}
          <div id="correcting-classifications" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <CheckCircle className="h-6 w-6 mr-3 text-primary-600" />
              Correcting Classifications
            </h2>
            
            <div className="space-y-4">
              <p className="text-gray-600">
                When you see orange (uncertain) detections, you should review and correct them:
              </p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-800">Click on Orange Detection</h4>
                  <p className="text-gray-600 text-sm">Click directly on the orange detection box to open the details panel</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800">Review Top Matches</h4>
                  <p className="text-gray-600 text-sm">Look at the "Top Matches" section and click on the correct classification</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800">Confirm Selection</h4>
                  <p className="text-gray-600 text-sm">The detection will turn purple with a ✓ checkmark, indicating your correction</p>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-purple-800 mb-2">User Correction Tooltip</h4>
                <p className="text-purple-700 text-sm">
                  Hover over purple "User Corrected" badges to see the original AI prediction vs. your correction.
                </p>
              </div>
            </div>
          </div>

          {/* Bulk Operations for Uncertain Detections */}
          <div id="bulk-operations" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Trash2 className="h-6 w-6 mr-3 text-orange-600" />
              Bulk Operations for Uncertain Detections
            </h2>
            
            <div className="space-y-4">
              <p className="text-gray-600">
                When you have many uncertain (orange) detections that are clearly incorrect, you can delete them all at once:
              </p>
              
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg border bg-orange-100 border-orange-300 text-orange-700">
                  <Trash2 className="h-4 w-4" />
                </div>
                <span className="text-sm text-gray-600">"Delete All Uncertain Detections" button (orange with cleaning brush icon)</span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-800">Review Current Image</h4>
                  <p className="text-gray-600 text-sm">Make sure the orange detections on the current image are indeed false positives</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800">Click "Delete All Uncertain"</h4>
                  <p className="text-gray-600 text-sm">The orange button with cleaning brush icon will delete all uncertain detections on this image</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800">Button Availability</h4>
                  <p className="text-gray-600 text-sm">The button is only active when there are uncertain detections on the current image</p>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="font-medium text-orange-800 mb-2">Important Notes</h4>
                    <ul className="text-orange-700 text-sm space-y-1">
                      <li>• This only affects uncertain detections on the current image</li>
                      <li>• Confident (green) and corrected (purple) detections are preserved</li>
                      <li>• This action cannot be undone - use with caution</li>
                      <li>• Review each image individually before bulk deletion</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Drawing Missing Detections */}
          <div id="drawing-detections" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <PenTool className="h-6 w-6 mr-3 text-primary-600" />
              Drawing Missing Detections
            </h2>
            
            <div className="space-y-6">
              <div id="manual-detection">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Manual Detection Drawing</h3>
                <p className="text-gray-600 mb-4">
                  If the AI missed an object, you can manually draw detection boxes:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <ActionButton icon={<PenTool className="h-4 w-4" />} label="Draw Detection" variant="draw" />
                    <span className="text-sm text-gray-600">Click this button to enter drawing mode</span>
                  </div>
                  
                  <div className="ml-14 space-y-3">
                    <p className="text-sm text-gray-600">• Click and drag to draw a rectangle around the missed object</p>
                    <p className="text-sm text-gray-600">• The system will analyze the drawn area and provide classification options</p>
                    <p className="text-sm text-gray-600">• Select the correct classification from the options provided</p>
                  </div>
                </div>
              </div>

              <div id="drawing-tips" className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Drawing Tips</h4>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Draw tight boxes around objects for better accuracy</li>
                  <li>• Minimum box size is 10x10 pixels</li>
                  <li>• User-drawn detections appear with blue borders and a ✏️ icon</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Deleting Wrong Detections */}
          <div id="deleting-detections" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Trash2 className="h-6 w-6 mr-3 text-red-600" />
              Deleting Wrong Detections
            </h2>
            
            <div className="space-y-4">
              <p className="text-gray-600">
                If the AI detected something incorrectly (false positive), you can remove it:
              </p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-800">Right-click Detection</h4>
                  <p className="text-gray-600 text-sm">Right-click on the detection box you want to delete</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800">Select "Delete Detection"</h4>
                  <p className="text-gray-600 text-sm">Choose the delete option from the context menu</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                  <div>
                    <p className="text-red-800 text-sm">
                      <strong>Warning:</strong> Deleted detections cannot be recovered. Only delete detections that are clearly incorrect.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isAdmin && (
            /* Algorithm Settings (Admin Only) */
            <div id="algorithm-settings" className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Settings className="h-6 w-6 mr-3 text-primary-600" />
                <div className="flex items-center">
                  <span>Advanced Algorithm Settings</span>
                  <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin Only
                  </span>
                </div>
              </h2>
            
            <div className="space-y-6">
              <div className="flex items-center space-x-4 mb-4">
                <ActionButton icon={<Settings className="h-4 w-4" />} label="Algorithm Settings" variant="settings" />
                <span className="text-sm text-gray-600">Click the settings button to access advanced options</span>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                  <div>
                    <p className="text-yellow-800 text-sm">
                      <strong>Advanced Users Only:</strong> These settings affect detection accuracy. Only modify if you understand the parameters.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div id="detection-parameters" className="space-y-4">
                  <h4 className="font-medium text-gray-800 text-blue-600">🔵 Detection Settings</h4>
                  <div className="space-y-3 text-sm bg-blue-50 p-4 rounded-lg">
                    <div>
                      <strong className="text-gray-700">Confidence Threshold:</strong>
                      <p className="text-gray-600">Minimum confidence for object detections (0.0-1.0)</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">IoU Threshold:</strong>
                      <p className="text-gray-600">Overlap threshold for duplicate detection removal</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Detection Image Size:</strong>
                      <p className="text-gray-600">Input resolution for the detection algorithm</p>
                    </div>
                  </div>
                </div>
                
                <div id="classification-parameters" className="space-y-4">
                  <h4 className="font-medium text-gray-800 text-purple-600">🟣 Recognition Settings</h4>
                  <div className="space-y-3 text-sm bg-purple-50 p-4 rounded-lg">
                    <div>
                      <strong className="text-gray-700">Cutoff Threshold:</strong>
                      <p className="text-gray-600">Minimum confidence for classification results</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Max Distance Ratio:</strong>
                      <p className="text-gray-600">Maximum distance ratio for feature matching</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Top-K Results:</strong>
                      <p className="text-gray-600">Number of classification options to show</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-800 text-yellow-600">🟡 Quality Assessment (Refiner)</h4>
                  <div className="space-y-3 text-sm bg-yellow-50 p-4 rounded-lg">
                    <div>
                      <strong className="text-gray-700">Enable Quality Assessment:</strong>
                      <p className="text-gray-600">Toggle to enable/disable the refiner model for quality scoring</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Refiner Threshold:</strong>
                      <p className="text-gray-600">Quality score threshold below which detections are uncertain (0.0-1.0)</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Refiner Trigger Threshold:</strong>
                      <p className="text-gray-600">YOLO confidence threshold below which refiner runs (0.0-1.0)</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-800 text-gray-600">⚪ General Settings</h4>
                  <div className="space-y-3 text-sm bg-gray-50 p-4 rounded-lg">
                    <div>
                      <strong className="text-gray-700">Show Uncertain Detections:</strong>
                      <p className="text-gray-600">Toggle to show/hide uncertain detections with dashed borders</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Color-Coded Sliders:</strong>
                      <p className="text-gray-600">All threshold sliders use gradient colors (red=0.0, green=1.0)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Staging Changes */}
          <div id="staging-changes" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <CheckSquare className="h-6 w-6 mr-3 text-green-600" />
              Staging Changes and Completion
            </h2>
            
            <div className="space-y-6">
              <div id="when-to-stage">
                <h3 className="text-lg font-medium text-gray-800 mb-3">When to Stage Changes</h3>
                <p className="text-gray-600 mb-4">
                  Stage changes when you've completed all corrections and are satisfied with the results:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2">✅ Ready to Stage</h4>
                    <ul className="text-green-700 text-sm space-y-1">
                      <li>• All detections are green (confident), purple (corrected), or blue (user-drawn)</li>
                      <li>• No orange (uncertain) detections remain anywhere in the session</li>
                      <li>• All images have been fully processed and reviewed</li>
                      <li>• Missing objects have been drawn and classified</li>
                      <li>• Wrong detections have been deleted or corrected</li>
                    </ul>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-medium text-orange-800 mb-2">⚠️ Not Ready</h4>
                    <ul className="text-orange-700 text-sm space-y-1">
                      <li>• <strong>Any orange detections exist anywhere in the session</strong></li>
                      <li>• Images are still being analyzed</li>
                      <li>• User-drawn detections haven't been corrected/confirmed</li>
                      <li>• Quality review is incomplete</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div id="staging-process">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Staging Process</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <ActionButton icon={<CheckSquare className="h-4 w-4" />} label="Stage Changes" variant="stage" />
                    <span className="text-sm text-gray-600">Click when all images are "green" (no uncertain detections)</span>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <ActionButton icon={<CheckSquare className="h-4 w-4" />} label="All images must be green" variant="stage" disabled />
                    <span className="text-sm text-gray-400">Button is disabled until all corrections are complete</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
                <h4 className="font-medium text-gray-800 mb-4">What Happens After Staging?</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-medium text-green-800 mb-2">✅ Session Completed</h5>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• Status changes to <StatusBadge status="completed" /></li>
                      <li>• Results are "frozen" and protected</li>
                      <li>• Data integrity is preserved</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-red-800 mb-2">🚫 Restrictions Apply</h5>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• No new image uploads</li>
                      <li>• Cannot modify detections</li>
                      <li>• Cannot delete results</li>
                      <li>• Read-only access only</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                  <div>
                    <p className="text-blue-800 text-sm">
                      <strong>Important:</strong> Staging changes is irreversible. Make sure all your work is complete before staging.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reports Management */}
          <div id="reports-management" className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <BarChart3 className="h-6 w-6 mr-3 text-primary-600" />
              Reports Management
            </h2>
            
            <div className="space-y-6">
              <div id="creating-reports">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Accessing Reports</h3>
                <p className="text-gray-600 mb-4">
                  View comprehensive analysis results from your completed sessions with detailed detection statistics and confidence scoring.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-800">Navigate to Reports</h4>
                    <p className="text-gray-600 text-sm">Click on "Reports" in the header navigation to access the analysis dashboard</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-800">Select Date and Rooms</h4>
                    <p className="text-gray-600 text-sm">Choose specific dates and rooms to view analysis statistics and session summaries</p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-800">Generate Detailed Report</h4>
                    <p className="text-gray-600 text-sm">Click "Generate Detailed Report" for comprehensive detection data export</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <h4 className="font-medium text-blue-800 mb-2">Available Information</h4>
                  <ul className="text-blue-700 text-sm space-y-1">
                    <li>• <strong>Detection Statistics:</strong> Total detections, confidence scores, and processing times</li>
                    <li>• <strong>Session Overview:</strong> Analysis results organized by sessions and rooms</li>
                    <li>• <strong>Quality Metrics:</strong> Accuracy measurements and user correction summaries</li>
                    <li>• <strong>Excel Export:</strong> Downloadable detailed data for further analysis</li>
                  </ul>
                </div>
              </div>

              <div id="data-export">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Data Export</h3>
                <p className="text-gray-600 mb-4">
                  Export detailed analysis data for further processing or archival purposes.
                </p>
                
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-4 mb-3">
                      <Download className="h-6 w-6 text-green-600" />
                      <h4 className="font-medium text-gray-800">Excel Export</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">Download comprehensive detection data in spreadsheet format</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Complete detection records with confidence scores</li>
                      <li>• User corrections and classifications</li>
                      <li>• Processing timestamps and metadata</li>
                      <li>• Session and room organization</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <h4 className="font-medium text-blue-800 mb-2">💡 Remote Re-Analysis</h4>
                  <p className="text-blue-700 text-sm">
                    Captured images can be revisited and re-processed remotely for continuous inventory accuracy. 
                    Export data allows for offline analysis and integration with external inventory systems.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {isAdmin && (
            /* Catalog Management (Admin Only) */
            <div id="catalog-management" className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <Database className="h-6 w-6 mr-3 text-primary-600" />
                <div className="flex items-center">
                  <span>Catalog Management</span>
                  <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin Only
                  </span>
                </div>
              </h2>
              
              <div className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <Shield className="h-5 w-5 text-purple-600 mt-0.5 mr-2" />
                    <div>
                      <p className="text-purple-800 text-sm">
                        <strong>Administrator Access Required:</strong> This section is only available to users with administrator privileges. These operations affect the core detection and classification system.
                      </p>
                    </div>
                  </div>
                </div>

                <div id="inventory-catalog">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Managing Inventory Catalog</h3>
                  <p className="text-gray-600 mb-4">
                    The inventory catalog contains all reference items used for classification. Manage classes, view statistics, and monitor index health.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-800">Access Catalog</h4>
                      <p className="text-gray-600 text-sm">Navigate to "Catalog" in the admin menu to view the inventory catalog</p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-800">View Class Statistics</h4>
                      <p className="text-gray-600 text-sm">Review the number of examples per class and index status</p>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-800">Monitor Index Health</h4>
                      <p className="text-gray-600 text-sm">Check for classes with insufficient examples or index corruption</p>
                    </div>
                  </div>
                </div>

                <div id="adding-items">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Adding New Items</h3>
                  <p className="text-gray-600 mb-4">
                    Expand your inventory catalog by adding new item classes with reference images.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <FolderPlus className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-gray-600">Click "Create Class" to add a new item category</span>
                    </div>
                    
                    <div className="ml-9 space-y-3">
                      <p className="text-sm text-gray-600">• Enter folder name (technical identifier) and display name (user-friendly label)</p>
                      <p className="text-sm text-gray-600">• Upload high-quality reference images (minimum 5 recommended)</p>
                      <p className="text-sm text-gray-600">• Rebuild the embedding index to include the new class</p>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                    <h4 className="font-medium text-yellow-800 mb-2">Best Practices for New Classes</h4>
                    <ul className="text-yellow-700 text-sm space-y-1">
                      <li>• Use clear, descriptive folder names (e.g., "luxury_watch_rolex")</li>
                      <li>• Provide varied angles and lighting conditions in reference images</li>
                      <li>• Ensure consistent image quality (high resolution, clear focus)</li>
                      <li>• Include at least 10-15 examples for better accuracy</li>
                    </ul>
                  </div>
                </div>

                <div id="editing-items">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Editing Existing Items</h3>
                  <p className="text-gray-600 mb-4">
                    Update class names, descriptions, and manage the reference images for existing catalog items.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <Edit3 className="h-5 w-5 text-blue-600" />
                      <span className="text-sm text-gray-600">Select a class and click the edit button to modify its properties</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h5 className="font-medium text-blue-800 mb-2">Editable Properties</h5>
                        <ul className="text-blue-700 text-sm space-y-1">
                          <li>• Display name</li>
                          <li>• Folder name (technical ID)</li>
                          <li>• Class description</li>
                          <li>• Reference image collection</li>
                        </ul>
                      </div>
                      
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h5 className="font-medium text-orange-800 mb-2">⚠️ Important Notes</h5>
                        <ul className="text-orange-700 text-sm space-y-1">
                          <li>• Changing folder names affects existing data</li>
                          <li>• Always rebuild index after edits</li>
                          <li>• Test classification accuracy after changes</li>
                          <li>• Keep backups of critical classes</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div id="adding-examples">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Adding More Examples</h3>
                  <p className="text-gray-600 mb-4">
                    Improve classification accuracy by adding more reference images to existing classes.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <ImageIcon className="h-5 w-5 text-indigo-600" />
                      <span className="text-sm text-gray-600">Open a class and use the "Upload Images" feature to add more examples</span>
                    </div>
                    
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <h4 className="font-medium text-indigo-800 mb-2">When to Add More Examples</h4>
                      <ul className="text-indigo-700 text-sm space-y-1">
                        <li>• Low classification confidence on similar items</li>
                        <li>• New product variations or styles emerge</li>
                        <li>• Seasonal or trending items need representation</li>
                        <li>• User corrections indicate missing reference cases</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div id="rebuilding-index">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Rebuilding Embedding Index</h3>
                  <p className="text-gray-600 mb-4">
                    The embedding index powers the classification system. Rebuild it after making changes to the inventory catalog.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <Settings className="h-5 w-5 text-red-600" />
                      <span className="text-sm text-gray-600">Use the "Rebuild Index" button with caution - this process takes time and affects system availability</span>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                        <div>
                          <h4 className="font-medium text-red-800 mb-2">Important Considerations</h4>
                          <ul className="text-red-700 text-sm space-y-1">
                            <li>• Index rebuilding can take 10-30 minutes depending on catalog size</li>
                            <li>• Classification functionality is temporarily unavailable during rebuild</li>
                            <li>• Schedule rebuilds during low-usage periods</li>
                            <li>• Always verify system functionality after rebuild completion</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Reference */}
          <div id="quick-reference" className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 shadow-sm border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Sliders className="h-6 w-6 mr-3 text-primary-600" />
              Quick Reference
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div id="keyboard-shortcuts">
                <h4 className="font-medium text-gray-800 mb-3">Keyboard Shortcuts</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Previous Image:</span>
                    <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">←</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Next Image:</span>
                    <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">→</kbd>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Close Details:</span>
                    <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">Esc</kbd>
                  </div>
                </div>
              </div>
              
              <div id="color-guide">
                <h4 className="font-medium text-gray-800 mb-3">Detection Color Guide</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded border border-green-600"></div>
                    <span className="text-gray-600"><strong>Green (Solid):</strong> Confident AI detection - high quality score</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-orange-500 rounded border-2 border-dashed border-orange-600"></div>
                    <span className="text-gray-600"><strong>Orange (Dashed):</strong> Uncertain detection - needs manual review</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-purple-500 rounded border border-purple-600 relative">
                      <span className="absolute -top-1 -right-1 text-white text-xs">✓</span>
                    </div>
                    <span className="text-gray-600"><strong>Purple (✓):</strong> User corrected classification</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded border border-blue-600 relative">
                      <span className="absolute -top-1 -right-1 text-white text-xs">✏️</span>
                    </div>
                    <span className="text-gray-600"><strong>Blue (✏️):</strong> User-drawn detection</span>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <h5 className="font-medium text-blue-800 text-sm mb-2">Detection State Logic:</h5>
                  <ul className="text-blue-700 text-xs space-y-1">
                    <li>• New user-drawn detections start as <strong>uncertain (orange)</strong></li>
                    <li>• After user selects classification → becomes <strong>user-drawn (blue)</strong></li>
                    <li>• Quality Assessment (Refiner) determines if detections are confident or uncertain</li>
                    <li>• Stage Changes button only enabled when <strong>no orange detections exist</strong></li>
                  </ul>
                </div>
              </div>
              
              <div id="need-help">
                <h4 className="font-medium text-gray-800 mb-3">Need Help?</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• Contact your administrator for room access</p>
                  <p>• Use tooltips for quick help on buttons</p>
                  <p>• Check this manual for detailed guidance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}

export default ManualPage